'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

// Animated board node preview
const PREVIEW_NODES = [
  { id: 'n1', x: 20, y: 25, color: '#7C3AED', size: 14, delay: 0 },
  { id: 'n2', x: 35, y: 25, color: '#7C3AED', size: 14, delay: 0.2 },
  { id: 'n3', x: 50, y: 25, color: '#7C3AED', size: 14, delay: 0.4 },
  { id: 'n4', x: 20, y: 50, color: '#7C3AED', size: 14, delay: 0.6 },
  { id: 'n5', x: 35, y: 50, color: '#059669', size: 14, delay: 0.8 },
  { id: 'n6', x: 50, y: 50, color: '#7C3AED', size: 14, delay: 0.3 },
  { id: 'n7', x: 20, y: 75, color: '#7C3AED', size: 14, delay: 0.5 },
  { id: 'n8', x: 35, y: 75, color: '#7C3AED', size: 14, delay: 0.7 },
  { id: 'n9', x: 50, y: 75, color: '#F59E0B', size: 20, delay: 0.1 },
  { id: 'n10', x: 65, y: 25, color: '#059669', size: 14, delay: 0.9 },
  { id: 'n11', x: 80, y: 25, color: '#059669', size: 14, delay: 0.15 },
  { id: 'n12', x: 50, y: 50, color: '#059669', size: 14, delay: 0.35 },
  { id: 'n13', x: 65, y: 50, color: '#059669', size: 14, delay: 0.55 },
  { id: 'n14', x: 80, y: 50, color: '#059669', size: 14, delay: 0.75 },
  { id: 'n15', x: 65, y: 75, color: '#059669', size: 14, delay: 0.95 },
  { id: 'n16', x: 80, y: 75, color: '#059669', size: 14, delay: 0.25 },
];

const PREVIEW_EDGES = [
  { id: 'e1', x1: 20, y1: 25, x2: 35, y2: 25 },
  { id: 'e2', x1: 35, y1: 25, x2: 50, y2: 25 },
  { id: 'e3', x1: 20, y1: 50, x2: 35, y2: 50 },
  { id: 'e4', x1: 35, y1: 50, x2: 50, y2: 50 },
  { id: 'e5', x1: 20, y1: 75, x2: 35, y2: 75 },
  { id: 'e6', x1: 35, y1: 75, x2: 50, y2: 75 },
  { id: 'e7', x1: 20, y1: 25, x2: 20, y2: 50 },
  { id: 'e8', x1: 20, y1: 50, x2: 20, y2: 75 },
  { id: 'e9', x1: 35, y1: 25, x2: 35, y2: 50 },
  { id: 'e10', x1: 35, y1: 50, x2: 35, y2: 75 },
  { id: 'e11', x1: 50, y1: 25, x2: 50, y2: 50 },
  { id: 'e12', x1: 50, y1: 50, x2: 50, y2: 75 },
  { id: 'e13', x1: 20, y1: 25, x2: 35, y2: 50 },
  { id: 'e14', x1: 35, y1: 25, x2: 20, y2: 50 },
  { id: 'e15', x1: 50, y1: 75, x2: 65, y2: 25 },
  { id: 'e16', x1: 65, y1: 25, x2: 80, y2: 25 },
  { id: 'e17', x1: 65, y1: 50, x2: 80, y2: 50 },
  { id: 'e18', x1: 65, y1: 75, x2: 80, y2: 75 },
  { id: 'e19', x1: 65, y1: 25, x2: 65, y2: 50 },
  { id: 'e20', x1: 65, y1: 50, x2: 65, y2: 75 },
  { id: 'e21', x1: 80, y1: 25, x2: 80, y2: 50 },
  { id: 'e22', x1: 80, y1: 50, x2: 80, y2: 75 },
];

export default function LandingHero() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [pulseIndex, setPulseIndex] = useState(0);

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setPulseIndex((i) => (i + 1) % PREVIEW_NODES?.length);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Top nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-5 lg:px-12">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-sans text-xl font-800 tracking-tight text-foreground">
            ViNCi
          </span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/game-setup"
            className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm
                       hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary"
          >
            Play Now
          </a>
        </div>
      </nav>
      {/* Hero content */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center gap-12 px-6 py-16 lg:px-12 max-w-screen-2xl mx-auto w-full">
        {/* Left: text */}
        <div className="flex-1 flex flex-col gap-8 max-w-2xl">
          <div className="flex items-center gap-2 w-fit">
            <span className="px-3 py-1 rounded-full text-xs font-600 bg-primary/20 text-secondary-foreground border border-primary/30 tracking-wide uppercase">
              Official Digital Edition
            </span>
          </div>

          <h1 className="text-hero-xl gradient-text-primary">
            The Art of<br />Strategic Formation
          </h1>

          <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
            ViNCi is a precision strategy board game where victory comes from mastering
            formations — Triangles, Lines, and Squares — across a dual-grid battlefield.
            2–5 players. One winner.
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => router?.push('/game-setup')}
              className="px-8 py-4 rounded-2xl bg-primary text-primary-foreground font-700 text-base
                         hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary
                         flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 5v10l7-5-7-5z" />
              </svg>
              Start a Game
            </button>
            <a
              href="#formations"
              className="px-8 py-4 rounded-2xl border border-border text-foreground font-600 text-base
                         hover:bg-secondary/50 active:scale-95 transition-all duration-150"
            >
              Learn Formations
            </a>
          </div>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-6 pt-4 border-t border-border">
            {[
              { label: 'Players', value: '2–5' },
              { label: 'Formation Types', value: '3' },
              { label: 'Board Positions', value: '17' },
              { label: 'AI Difficulty', value: '3 Levels' },
            ]?.map((stat) => (
              <div key={`stat-${stat?.label}`} className="flex flex-col gap-0.5">
                <span className="font-mono-game text-2xl font-700 text-primary">
                  {stat?.value}
                </span>
                <span className="text-xs font-500 text-muted-foreground uppercase tracking-wider">
                  {stat?.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: animated board preview */}
        <div className="flex-shrink-0 w-full max-w-sm lg:max-w-md xl:max-w-lg">
          <div className="relative glass-card rounded-3xl p-6 aspect-square">
            {/* Glow backdrop */}
            <div className="absolute inset-0 rounded-3xl overflow-hidden">
              <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 bg-accent/15 rounded-full blur-2xl" />
            </div>

            <svg
              viewBox="0 0 100 100"
              className="w-full h-full relative z-10"
              style={{ overflow: 'visible' }}
            >
              {/* Edges */}
              {PREVIEW_EDGES?.map((edge) => (
                <line
                  key={edge?.id}
                  x1={edge?.x1}
                  y1={edge?.y1}
                  x2={edge?.x2}
                  y2={edge?.y2}
                  stroke="rgba(124,58,237,0.25)"
                  strokeWidth="0.5"
                />
              ))}

              {/* Nodes */}
              {PREVIEW_NODES?.map((node, i) => {
                const isPulsing = mounted && i === pulseIndex % PREVIEW_NODES?.length;
                const isSpot9 = node?.id === 'n9';
                return (
                  <g key={node?.id}>
                    {isPulsing && (
                      <circle
                        cx={node?.x}
                        cy={node?.y}
                        r={node?.size / 2 + 4}
                        fill="none"
                        stroke={node?.color}
                        strokeWidth="0.8"
                        opacity="0.5"
                        style={{ animation: 'nodePulse 1s ease-in-out' }}
                      />
                    )}
                    {isSpot9 && (
                      <circle
                        cx={node?.x}
                        cy={node?.y}
                        r={node?.size / 2 + 6}
                        fill="none"
                        stroke="#F59E0B"
                        strokeWidth="0.6"
                        opacity="0.4"
                        style={{ animation: 'nodePulse 2s ease-in-out infinite' }}
                      />
                    )}
                    <circle
                      cx={node?.x}
                      cy={node?.y}
                      r={node?.size / 2}
                      fill={node?.color}
                      opacity={isPulsing ? 1 : 0.85}
                      style={{
                        filter: `drop-shadow(0 0 ${isSpot9 ? 4 : 2}px ${node?.color})`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                    {isSpot9 && (
                      <text
                        x={node?.x}
                        y={node?.y + 1.5}
                        textAnchor="middle"
                        fill="white"
                        fontSize="4"
                        fontWeight="700"
                        fontFamily="Manrope, sans-serif"
                      >
                        9
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Formation highlight — triangle */}
              <polygon
                points="20,25 35,50 20,75"
                fill="rgba(245,158,11,0.08)"
                stroke="rgba(245,158,11,0.5)"
                strokeWidth="0.5"
                strokeDasharray="2,1"
              />
            </svg>

            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
              <span className="px-2.5 py-1 rounded-lg text-xs font-600 bg-accent/20 text-accent border border-accent/30">
                Triangle +1
              </span>
              <span className="px-2.5 py-1 rounded-lg text-xs font-600 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                Line +3
              </span>
              <span className="px-2.5 py-1 rounded-lg text-xs font-600 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Square +2
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="relative z-10 flex justify-center pb-8">
        <div className="flex flex-col items-center gap-2 opacity-50">
          <span className="text-xs text-muted-foreground font-500">Scroll to explore</span>
          <div className="w-5 h-8 rounded-full border border-border flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-muted-foreground rounded-full" style={{ animation: 'slideUp 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      </div>
    </section>
  );
}